import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmStatusModal from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Item } from "../../../../../../../models/item";
import { OnboardingStage } from "../../../../../../../models/onboarding.stage";
import { Room } from "../../../../../../../models/room";
import { ServiceBooking as PackingMaterial } from "../../../../../../../models/service.booking";
import { Row } from "../../../../../../../models/shipeezi.table";
import { handleGetConvertValue } from "../../../../../../../util/converts";
import { Status } from "../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import { ItemModal } from "./ItemModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

const hardwareBoxIcon = require("Assets/img/hardwareBox-icon.svg");
const highRiskIcon = require("Assets/img/highRisk-icon.svg");
const meIcon = require("Assets/img/me-icon.svg");
const proGearIcon = require("Assets/img/proGear-icon.svg");

interface Props {
  onboardingStage?: OnboardingStage;
  refetchIsoCertificate: () => void;
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const ItemPage = ({ onboardingStage, refetchIsoCertificate, permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    items,
    total,
    page,
    pageSize,
    hasErrors,
    isLoading,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createItem,
    updateItem,
    updateItemStatus,
    updateItemList,
  } = useActions(refetchIsoCertificate, onboardingStage);

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [itemSelected, setItemSelected] = useState<Item | undefined>();
  const [isViewItemDetails, setIsViewItemDetails] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [cleanSelection, setCleanSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
    `}
    >
      {status}
    </Badge>
  );

  const editItem = (index: number) => {
    const itemToUpdate = items.find((item) => Number(item.id) === Number(index));

    if (itemToUpdate) {
      if (hasErrors !== undefined) setHasErrors(undefined);
      setIsViewItemDetails(false);
      setItemSelected(itemToUpdate);
      setOpenCreateModal(true);
    }
  };

  const viewItem = (index: number) => {
    const item = items.find((item) => Number(item.id) === Number(index));

    return (
      <Tooltip title={translate("view") || ""} placement="top">
        <div>
          <FiEye
            size={18}
            color=""
            className={classes.button}
            onClick={() => {
              if (item) {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setItemSelected(item);
                setIsViewItemDetails(true);
                setOpenCreateModal(true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const changeStatusItem = (index: number) => {
    const itemToUpdate = items.find((item) => Number(item.id) === Number(index));

    if (itemToUpdate) {
      updateItemStatus({
        id: index,
        item: {
          ...itemToUpdate,
          status: itemToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveItemSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, items);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    {
      name: "name",
      title: translate("name"),
      formatComponent: (value: string, row: Item) => {
        const tags = [];

        if (row.isHardwareBox) {
          tags.push(
            <Tooltip placement="top" title={translate("hardwareBox")}>
              <img className={classes.tags} src={hardwareBoxIcon} />
            </Tooltip>
          );
        }

        if (row.mandatoryME) {
          tags.push(
            <Tooltip placement="top" title={translate("me")}>
              <img className={classes.tags} src={meIcon} />
            </Tooltip>
          );
        }

        if (row.highRisk) {
          tags.push(
            <Tooltip placement="top" title={translate("highRiskHighValue")}>
              <img className={classes.tags} src={highRiskIcon} />
            </Tooltip>
          );
        }

        if (row.isProGear) {
          tags.push(
            <Tooltip placement="top" title={translate("proGear")}>
              <img className={classes.tags} src={proGearIcon} />
            </Tooltip>
          );
        }

        return (
          <>
            <span>{value}</span>
            {tags.map((tag) => tag)}
          </>
        );
      },
    },
    {
      name: "volume",
      title: translate("volume"),
      formatText: (volume: number) =>
        `${handleGetConvertValue(
          volume,
          getMeasurement()?.code,
          getMeasurement()?.decimalPlaces
        )} ${getMeasurement()?.code}`,
    },
    {
      name: "rooms",
      title: translate("location"),
      hideSort: true,
      hideSearch: true,
      formatText: (rooms: Room[]) => rooms.map((room: Room) => room.name).join(" - "),
    },
    // TODO Felipe check with Carlos if in screen, use relation between item and item_type (packaging type)
    // {
    //   name: "packagings",
    //   title: translate("packaging"),
    //   hideSort: true,
    //   formatText: (packagings: ItemTypes[]) =>
    //     packagings.map((item: ItemTypes) => item.name).join(" - ")
    // },
    {
      name: "packingMaterials",
      title: translate("materials"),
      hideSort: true,
      hideSearch: true,
      formatText: (materials: PackingMaterial[]) =>
        materials.map((material) => material.name).join(" - "),
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => {
          setIsViewItemDetails(false);
          if (hasErrors !== undefined) setHasErrors(undefined);
          setItemSelected(undefined);
          setOpenCreateModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        { name: translate("code"), column: "code" },
        { name: translate("name"), column: "name" },
        { name: translate("volume"), column: "volume" },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderButtonToChangeStatus(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request-success-item-change",
      });
      setHasErrors(undefined);
      setCleanSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  const handleChangeStatusTo = (statusChoice: string) => {
    const statusOptionsList = {
      [Status.ACTIVE.toLowerCase()]: Status.ACTIVE,
      [Status.INACTIVE.toLowerCase()]: Status.INACTIVE,
      invert: "invert",
    };
    const status = statusOptionsList[statusChoice];

    const items = [...rows].map((row) => {
      if (status === "invert")
        return {
          id: row.id,
          status: row.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        };

      return { id: row.id, status };
    });

    updateItemList(items);
    setCleanSelection(true);
  };

  return (
    <div data-cy="itemsListTable">
      <ShipeeziNewGrid
        data={items}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={cleanSelection}
        handleSelect={(rows: number[]) => handleSaveItemSelected(rows)}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (itemId) => editItem(Number(itemId))
            : undefined,
          handleChange: (itemId) => changeStatusItem(Number(itemId)),
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewItem }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openCreateModal && (
        <ItemModal
          itemToUpdate={itemSelected}
          setIsOnViewMode={() => setIsViewItemDetails((state) => !state)}
          open={openCreateModal}
          errors={hasErrors}
          isLoading={isLoading}
          isView={isViewItemDetails}
          closeModal={() => setOpenCreateModal(false)}
          submit={(item: Item) => {
            if (itemSelected && itemSelected.id)
              updateItem({
                id: itemSelected.id,
                item: { ...itemSelected, ...item },
              });
            else
              createItem({
                ...item,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmStatusModal
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          submit={handleChangeStatusTo}
        />
      )}
    </div>
  );
};

export default ItemPage;
